import React, { useState, useEffect } from 'react';
import {
  RefreshCw, CheckCircle, AlertTriangle, XCircle, Globe, FileSearch,
  ExternalLink, ChevronDown, ChevronUp, AlertCircle, Loader2
} from 'lucide-react';

const BrandWebsiteTermsTab = ({ project }) => {
  const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [pages, setPages] = useState([]);
  const [expandedPages, setExpandedPages] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    if (project?.id) {
      fetchBrandWebsiteTerms();
    }
  }, [project?.id]);

  const fetchBrandWebsiteTerms = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${basePath}/api/terms-checker/${project.id}/brand-website`,
        {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus(data.status || {});
        setSummary(data.summary || null);
        setPages(data.pages || []);
      } else {
        setError(data.message || 'Failed to fetch brand website terms');
      }
    } catch (err) {
      console.error('Error fetching brand website terms:', err);
      setError('Failed to fetch brand website terms');
    } finally {
      setIsLoading(false);
    }
  };

  const triggerCheck = async () => {
    setIsChecking(true);
    setError(null);
    try {
      const response = await fetch(
        `${basePath}/api/terms-checker/${project.id}/check-brand-website`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        // Poll for results
        pollForResults();
      } else {
        setError(data.message || 'Failed to start brand website check');
        setIsChecking(false);
      }
    } catch (err) {
      console.error('Error triggering brand website check:', err);
      setError('Failed to start brand website check');
      setIsChecking(false);
    }
  };

  const pollForResults = () => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(
          `${basePath}/api/terms-checker/${project.id}/brand-website`,
          {
            method: 'GET',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );

        const data = await response.json();

        if (response.ok && data.success) {
          setStatus(data.status || {});
          setSummary(data.summary || null);
          setPages(data.pages || []);

          if (data.status?.brand_check_status === 'completed' || data.status?.brand_check_status === 'failed') {
            clearInterval(pollInterval);
            setIsChecking(false);
          }
        }
      } catch (err) {
        console.error('Error polling:', err);
      }
    }, 5000); // Poll every 5 seconds

    // Stop polling after 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval);
      setIsChecking(false);
    }, 300000);
  };

  const togglePage = (pageId) => {
    setExpandedPages(prev => ({
      ...prev,
      [pageId]: !prev[pageId]
    }));
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'major':
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      case 'minor':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      default:
        return <CheckCircle className="w-5 h-5 text-green-500" />;
    }
  };

  const getSeverityBadge = (severity, isConsistent) => {
    if (isConsistent === true || isConsistent === null) {
      return (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700 border border-green-200">
          {isConsistent === null ? 'No Terms' : 'Consistent'}
        </span>
      );
    }

    const styles = {
      critical: 'bg-red-100 text-red-700 border-red-200',
      major: 'bg-orange-100 text-orange-700 border-orange-200',
      minor: 'bg-yellow-100 text-yellow-700 border-yellow-200'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${styles[severity] || styles.minor}`}>
        {severity?.charAt(0).toUpperCase() + severity?.slice(1)} Issue
      </span>
    );
  };

  const getPageTypeBadge = (pageType) => {
    const styles = {
      terms_page: 'bg-blue-100 text-blue-700',
      bonus_page: 'bg-purple-100 text-purple-700',
      promotion_page: 'bg-pink-100 text-pink-700',
      homepage: 'bg-gray-100 text-gray-700',
      faq_page: 'bg-cyan-100 text-cyan-700',
      welcome_page: 'bg-green-100 text-green-700',
      other: 'bg-gray-100 text-gray-500'
    };

    const labels = {
      terms_page: 'Terms',
      bonus_page: 'Bonus',
      promotion_page: 'Promo',
      homepage: 'Home',
      faq_page: 'FAQ',
      welcome_page: 'Welcome',
      terms_related: 'Related',
      other: 'Other'
    };

    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded ${styles[pageType] || styles.other}`}>
        {labels[pageType] || 'Other'}
      </span>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading brand website check...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Globe className="w-6 h-6 text-blue-600" />
            <div>
              <h3 className="font-medium text-gray-800">Brand Website Consistency</h3>
              <p className="text-sm text-gray-500">
                Check if terms are consistent across your own website
              </p>
            </div>
          </div>
          <button
            onClick={triggerCheck}
            disabled={isChecking || status?.brand_check_status === 'processing'}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
          >
            {isChecking || status?.brand_check_status === 'processing' ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Checking...
              </>
            ) : (
              <>
                <FileSearch className="w-4 h-4" />
                {pages.length > 0 ? 'Re-check Website' : 'Check Website'}
              </>
            )}
          </button>
        </div>

        {/* Stats */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="text-2xl font-bold text-blue-600">{summary.total_pages_checked || 0}</div>
              <div className="text-xs text-gray-500">Pages Checked</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="text-2xl font-bold text-green-600">{summary.consistent_pages || 0}</div>
              <div className="text-xs text-gray-500">Consistent</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="text-2xl font-bold text-red-600">{summary.inconsistent_pages || 0}</div>
              <div className="text-xs text-gray-500">Inconsistent</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="flex items-center gap-1">
                <span className="text-lg font-bold text-red-600">{summary.critical_issues || 0}</span>
                <span className="text-gray-400">/</span>
                <span className="text-lg font-bold text-orange-600">{summary.major_issues || 0}</span>
                <span className="text-gray-400">/</span>
                <span className="text-lg font-bold text-yellow-600">{summary.minor_issues || 0}</span>
              </div>
              <div className="text-xs text-gray-500">Critical/Major/Minor</div>
            </div>
          </div>
        )}

        {status?.brand_check_status === 'processing' && (
          <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
              <span className="text-sm text-amber-700">
                Checking your website... This may take a few minutes.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
          <div className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Pages List */}
      {pages.length > 0 ? (
        <div className="space-y-2">
          <h4 className="font-medium text-gray-700 mb-3">
            Pages Analyzed ({pages.length})
          </h4>

          {/* Inconsistent pages first */}
          {pages.filter(p => p.is_consistent === false).length > 0 && (
            <div className="mb-4">
              <h5 className="text-sm font-medium text-red-600 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Pages with Inconsistencies
              </h5>
              {pages.filter(p => p.is_consistent === false).map((page) => (
                <PageRow
                  key={page.id}
                  page={page}
                  isExpanded={expandedPages[page.id]}
                  onToggle={() => togglePage(page.id)}
                  getSeverityIcon={getSeverityIcon}
                  getSeverityBadge={getSeverityBadge}
                  getPageTypeBadge={getPageTypeBadge}
                />
              ))}
            </div>
          )}

          {/* Consistent pages */}
          {pages.filter(p => p.is_consistent !== false).length > 0 && (
            <div>
              <h5 className="text-sm font-medium text-green-600 mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Consistent Pages
              </h5>
              {pages.filter(p => p.is_consistent !== false).map((page) => (
                <PageRow
                  key={page.id}
                  page={page}
                  isExpanded={expandedPages[page.id]}
                  onToggle={() => togglePage(page.id)}
                  getSeverityIcon={getSeverityIcon}
                  getSeverityBadge={getSeverityBadge}
                  getPageTypeBadge={getPageTypeBadge}
                />
              ))}
            </div>
          )}
        </div>
      ) : status?.brand_check_status !== 'processing' && (
        <div className="text-center py-12">
          <Globe className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-700 mb-2">No Pages Checked Yet</h4>
          <p className="text-gray-500 mb-4">
            Click "Check Website" to scan your brand's website for terms consistency
          </p>
        </div>
      )}
    </div>
  );
};

// Separate component for each page row
const PageRow = ({ page, isExpanded, onToggle, getSeverityIcon, getSeverityBadge, getPageTypeBadge }) => {
  const extractedTerms = typeof page.extracted_terms === 'string'
    ? JSON.parse(page.extracted_terms || '{}')
    : (page.extracted_terms || {});

  const inconsistencyDetails = typeof page.inconsistency_details === 'string'
    ? JSON.parse(page.inconsistency_details || '[]')
    : (page.inconsistency_details || []);

  return (
    <div className="bg-white border border-gray-200 rounded-lg mb-2 overflow-hidden">
      {/* Row Header */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          {getSeverityIcon(page.is_consistent === false ? page.severity : null)}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {getPageTypeBadge(page.page_type)}
              <span className="font-medium text-gray-800 truncate">
                {page.page_title || new URL(page.page_url).pathname}
              </span>
              <a
                href={page.page_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-blue-600 hover:text-blue-800"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
            <p className="text-xs text-gray-500 truncate max-w-lg">
              {page.page_url}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {getSeverityBadge(page.severity, page.is_consistent)}
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 py-4 border-t border-gray-100 bg-gray-50">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Terms Found on Page */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Terms Found on This Page</h4>
              {extractedTerms.termsFound ? (
                <div className="space-y-2 text-sm">
                  {extractedTerms.welcomeBonus && (
                    <div><span className="font-medium text-gray-600">Welcome Bonus:</span> {extractedTerms.welcomeBonus}</div>
                  )}
                  {extractedTerms.wageringRequirement && (
                    <div><span className="font-medium text-gray-600">Wagering:</span> {extractedTerms.wageringRequirement}</div>
                  )}
                  {extractedTerms.minimumDeposit && (
                    <div><span className="font-medium text-gray-600">Min Deposit:</span> {extractedTerms.minimumDeposit}</div>
                  )}
                  {extractedTerms.maximumBonus && (
                    <div><span className="font-medium text-gray-600">Max Bonus:</span> {extractedTerms.maximumBonus}</div>
                  )}
                  {extractedTerms.freeSpins?.count && (
                    <div><span className="font-medium text-gray-600">Free Spins:</span> {extractedTerms.freeSpins.count}</div>
                  )}
                  {extractedTerms.timeLimit && (
                    <div><span className="font-medium text-gray-600">Time Limit:</span> {extractedTerms.timeLimit}</div>
                  )}
                  {extractedTerms.maxBet && (
                    <div><span className="font-medium text-gray-600">Max Bet:</span> {extractedTerms.maxBet}</div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 text-sm">No bonus terms found on this page</p>
              )}
            </div>

            {/* Inconsistencies */}
            {page.is_consistent === false && inconsistencyDetails.length > 0 && (
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="text-sm font-medium text-red-700 mb-3">Inconsistencies Found</h4>
                <div className="space-y-2">
                  {inconsistencyDetails.map((inc, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <AlertTriangle className={`w-4 h-4 mt-0.5 ${inc.severity === 'critical' ? 'text-red-500' : inc.severity === 'major' ? 'text-orange-500' : 'text-yellow-500'}`} />
                      <div>
                        <span className="font-medium">{inc.term}:</span>
                        <div className="text-gray-600">
                          Official: <span className="text-blue-600">{inc.official}</span>
                        </div>
                        <div className="text-gray-600">
                          This page: <span className="text-red-600">{inc.pageValue}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* AI Notes */}
            {page.ai_notes && (
              <div className={`${page.is_consistent === false ? 'lg:col-span-2' : ''} bg-gray-100 p-4 rounded-lg`}>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Analysis Notes</h4>
                <p className="text-sm text-gray-600">{page.ai_notes}</p>
              </div>
            )}
          </div>

          {page.checked_at && (
            <p className="text-xs text-gray-400 mt-3 text-right">
              Checked: {new Date(page.checked_at).toLocaleString()}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default BrandWebsiteTermsTab;
